import React from "react";
import { TimeSelect } from "@v2/components/form/TimeSelect/TimeSelect";
import { TimeSelectProps } from "@v2/components/form/TimeSelect/types";
import { getNowDate } from "@gettactic/helpers/src/date-time/getNowDate";
import { injectTimeIntoDate } from "@gettactic/helpers/src/date-time/injectTimeIntoDate";
import {
  addMinutes,
  format,
  isAfter,
  isBefore,
  subMinutes,
  min
} from "date-fns";
import { Button } from "@v2/components/ui/button";
import { LiveIcon } from "@v2/icons";
import { PulsingNode } from "@cmp-src/components/PulsingNode/PulsingNode";
import { cn, StartEndTimeMode } from "@gettactic/helpers";
import { MIN_TIME_ADJUSTMENT_MINUTES } from "../constants";

export interface StartEndTimePickerProps {
  startDate?: Date | null;
  endDate?: Date | null;
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  startTimeSelectProps?: Partial<TimeSelectProps>;
  endTimeSelectProps?: Partial<TimeSelectProps>;
  mode?: StartEndTimeMode;
  allowLive?: boolean;
  onModeChange?: (mode: StartEndTimeMode) => void;
  className?: string;
}

const radius = "rounded-v2 md:rounded-v2";
const timeFormat = "hh:mm a";
const classes = {
  Wrapper:
    `relative w-full flex bg-neutral-100 ${radius} h-[42px] mt-1 shadow-lg md:shadow-none`,
  AnimatedBackground:
    `absolute top-0 bottom-0 w-10 ${radius} bg-white transition-all border-1 border-neutral-200`,
  TimeTrigger:
    "w-full bg-transparent border-0 hover:bg-transparent text-[12px] md:text-[14px]",
  TimePickersWrapper: "w-full flex relative",
  TimeSelectWrapper: "p-0 m-0",
  LiveButton:
    "h-[42px] md:min-w-[78px] font-[500] text-[12px] md:text-[14px] text-neutral-700 hover:bg-transparent"
};

export const StartEndTimePicker: React.FC<StartEndTimePickerProps> = (
  props
) => {
  const {
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    startTimeSelectProps,
    endTimeSelectProps,
    mode = "manual",
    allowLive = false,
    onModeChange,
    className
  } = props;

  const handleSetTimeStart = (time?: string) => {
    const now = getNowDate();
    const date = startDate || now;
    const newStartDate = injectTimeIntoDate(date, time || "00:00");
    onStartDateChange(newStartDate);
    onModeChange?.("manual");

    if (endDate && isAfter(newStartDate, endDate)) {
      const endOfDayDate = new Date(date);
      endOfDayDate.setHours(23, 59, 0, 0);
      const adjustedEndDate = addMinutes(newStartDate, MIN_TIME_ADJUSTMENT_MINUTES);
      onEndDateChange(min([adjustedEndDate, endOfDayDate]));
    }
  };

  const handleSetTimeEnd = (time?: string) => {
    const now = getNowDate();
    const date = endDate || now;
    const newEndDate = injectTimeIntoDate(date, time || "00:00");
    onEndDateChange(newEndDate);
    onModeChange?.("manual");

    if (startDate && isBefore(newEndDate, startDate)) {
      const adjustedStartDate = subMinutes(newEndDate, MIN_TIME_ADJUSTMENT_MINUTES);
      onStartDateChange(adjustedStartDate);
    }
  };

  const startTimeValue = React.useMemo(() => {
    return startDate ? format(startDate, timeFormat) : "";
  }, [startDate]);

  const endTimeValue = React.useMemo(() => {
    return endDate ? format(endDate, timeFormat) : "";
  }, [endDate]);

  const [timeSelectorsWrapperRef, setTimeSelectorsWrapperRef] =
    React.useState<HTMLDivElement | null>(null);

  const [liveSelectorWrapperRef, setLiveSelectorWrapperRef] =
    React.useState<HTMLDivElement | null>(null);

  const [backgroundRef, setBackgroundRef] =
    React.useState<HTMLDivElement | null>(null);

  React.useLayoutEffect(() => {
    if (!backgroundRef) return;

    const updateBackgroundPosition = () => {
      if (!backgroundRef) return;

      let width = 0;
      let offsetLeft = 0;

      if (mode === "live" && liveSelectorWrapperRef) {
        width = liveSelectorWrapperRef.getBoundingClientRect()?.width || 0;
        offsetLeft = liveSelectorWrapperRef.offsetLeft;
      } else if (timeSelectorsWrapperRef) {
        width = timeSelectorsWrapperRef.getBoundingClientRect()?.width || 0;
        offsetLeft = timeSelectorsWrapperRef.offsetLeft;
      }

      backgroundRef.style.width = `${width}px`;
      backgroundRef.style.left = `${offsetLeft}px`;
    };

    // Initial measurement
    updateBackgroundPosition();

    // In some contexts (e.g. animated dialogs), the parent container is still
    // animating its scale/position when we first measure. Re-measure shortly
    // after mount to capture the final size.
    const timeoutId =
      typeof window !== "undefined"
        ? window.setTimeout(updateBackgroundPosition, 210)
        : undefined;

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    timeSelectorsWrapperRef,
    backgroundRef,
    mode,
    liveSelectorWrapperRef,
    startTimeValue,
    endTimeValue
  ]);

  const isLive = mode === "live";

  return (
    <div className={cn(classes.Wrapper, className)}>
      <div className={classes.AnimatedBackground} ref={setBackgroundRef} />
      <div
        className={classes.TimePickersWrapper}
        ref={setTimeSelectorsWrapperRef}
      >
        <TimeSelect
          wrapperClassName={classes.TimeSelectWrapper}
          value={startTimeValue}
          onSelect={handleSetTimeStart}
          triggerProps={{ className: classes.TimeTrigger }}
          {...startTimeSelectProps}
        />
        <TimeSelect
          wrapperClassName={classes.TimeSelectWrapper}
          value={endTimeValue}
          onSelect={handleSetTimeEnd}
          triggerProps={{ className: classes.TimeTrigger }}
          {...endTimeSelectProps}
        />
        {isLive && (
          <button
            onClick={() => onModeChange?.("manual")}
            className={"absolute inset-0"}
          />
        )}
      </div>
      {allowLive && (
        <div className={"relative"} ref={setLiveSelectorWrapperRef}>
          <Button
            variant={"ghost"}
            className={cn(
              classes.LiveButton,
              isLive && "text-primary-1000 hover:text-primary-1000"
            )}
            onClick={() => {
              onModeChange?.("live");
            }}
          >
            <PulsingNode isDisabled={!isLive}>
              <LiveIcon className={"size-3.5"} />
            </PulsingNode>
            <span className={"hidden md:inline"}>Live</span>
          </Button>
        </div>
      )}
    </div>
  );
};
